import { z } from 'zod';
import { InteractionProvider } from '../index.js';
import { InteractionEventType,
// Note: Mapping Voice events to the closest Interaction types (Delivered=Completed, Opened=Answered)
 } from './index.js';
// --- Shared Sub-schemas ---
const VonageDtmfSchema = z.object({
    digits: z.string().optional(),
    timed_out: z.boolean().optional(),
});
const VonageSpeechSchema = z.object({
    results: z.array(z.unknown()).optional(), // specific speech structure not detailed in logs
});
export const VonageInboundDataSchema = z.object({
    to: z.string(),
    from: z.string(),
    uuid: z.string(),
    region_url: z.string(),
    endpoint_type: z.string().optional(),
    conversation_uuid: z.string(),
});
// --- Base Schemas ---
const BaseVonageEvent = z.object({
    timestamp: z.string().optional(),
    conversation_uuid: z.string().optional(),
});
// --- 1. Outbound Call Lifecycle Events (Discriminator: status) ---
const BaseCallStatusData = BaseVonageEvent.extend({
    to: z.string(),
    from: z.string(),
    uuid: z.string(),
    direction: z.enum(['outbound', 'inbound']),
    headers: z.record(z.string(), z.string()).optional(),
    network: z.string().nullable().optional(),
});
// 1.1 Started / Ringing / Answered
export const VonageCallProgressEventBronzeSchema = BaseCallStatusData.extend({
    status: z.enum(['started', 'ringing', 'answered']),
    start_time: z.string().nullable().optional(),
    rate: z.string().nullable().optional(),
});
// 1.2 Machine / Human Detection
export const VonageCallAnalysisEventBronzeSchema = BaseVonageEvent.extend({
    to: z.string(),
    from: z.string(),
    status: z.enum(['machine', 'human']),
    call_uuid: z.string(), // Note: logs use call_uuid here instead of uuid
    sub_state: z.string().optional(), // e.g., "beep_start", "beep_timeout"
});
// 1.3 Completed (Success)
export const VonageCallCompletedEventBronzeSchema = BaseCallStatusData.extend({
    status: z.literal('completed'),
    duration: z.string(),
    end_time: z.string().optional(),
    start_time: z.string().nullable().optional(),
    rate: z.string(),
    price: z.string(),
    detail: z.string().optional(),
    sip_code: z.number().or(z.string()).optional(),
    disconnected_by: z.string().optional(),
});
// 1.4 Failed / Busy / Cancelled (Errors)
export const VonageCallErrorEventBronzeSchema = BaseCallStatusData.extend({
    status: z.enum(['failed', 'busy', 'cancelled', 'rejected', 'unanswered']),
    detail: z.string().optional(), // e.g. "internal_error", "remote_busy", "ring_timeout"
    sip_code: z.number().or(z.string()).optional(), // 500, 486, 487
});
// --- 2. Inbound Events ---
export const VonageInboundEventBronzeSchema = BaseVonageEvent.extend({
    type: z.literal('inbound'),
    data: VonageInboundDataSchema,
});
// --- 3. Recording Events ---
export const VonageRecordingEventBronzeSchema = BaseVonageEvent.extend({
    size: z.number(),
    recording_url: z.string(),
    recording_uuid: z.string(),
    start_time: z.string(),
    end_time: z.string(),
});
// --- 4. Input Events (DTMF/Speech) ---
export const VonageInputEventBronzeSchema = BaseVonageEvent.extend({
    dtmf: VonageDtmfSchema.optional(),
    speech: VonageSpeechSchema.optional(),
    uuid: z.string().nullable().optional(),
    id: z.string().optional(),
    createdAt: z.string().optional(),
});
// --- Wrapper Schemas & Types ---
export const VonageCallCompletedEventSchema = z.object({
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: VonageCallCompletedEventBronzeSchema,
});
export const VonageCallErrorEventSchema = z.object({
    provider: z.literal(InteractionProvider.VonageCall),
    bronzeData: VonageCallErrorEventBronzeSchema,
});
// --- Mapping Function ---
export const mapVonageCallEvent = (bronzeEvent) => {
    if ('type' in bronzeEvent && bronzeEvent.type === 'inbound') {
        return {
            provider: InteractionProvider.VonageCall,
            bronzeData: bronzeEvent,
            type: InteractionEventType.Inbound,
            data: {},
        };
    }
    if ('recording_url' in bronzeEvent) {
        // We currently have no use case for call recording events, so we skip them.
        return null;
    }
    if ('dtmf' in bronzeEvent || 'speech' in bronzeEvent) {
        if (typeof bronzeEvent.dtmf?.digits === 'string' &&
            bronzeEvent.dtmf.timed_out === false) {
            return {
                provider: InteractionProvider.VonageCall,
                bronzeData: bronzeEvent,
                type: InteractionEventType.DigitsEntered,
                data: {
                    digits: bronzeEvent.dtmf.digits,
                    timedOut: bronzeEvent.dtmf.timed_out,
                },
            };
        }
    }
    if ('status' in bronzeEvent) {
        switch (bronzeEvent.status) {
            // 'started' fires when Vonage creates the call leg internally, before it
            // reaches the phone network. It carries no meaningful information beyond
            // what is already known at call creation time, so we discard it.
            case 'started':
                return null;
            // 'ringing' fires when the call has been handed to the phone network and
            // the destination phone is actually ringing — the first event that is
            // visible/meaningful to the end user.
            case 'ringing':
                return {
                    provider: InteractionProvider.VonageCall,
                    bronzeData: bronzeEvent,
                    type: InteractionEventType.Ringing,
                    data: {},
                };
            case 'answered':
                return {
                    provider: InteractionProvider.VonageCall,
                    bronzeData: bronzeEvent,
                    type: InteractionEventType.Answered,
                    data: {},
                };
            case 'completed':
                return {
                    provider: InteractionProvider.VonageCall,
                    bronzeData: bronzeEvent,
                    type: InteractionEventType.Completed,
                    data: {
                        duration: bronzeEvent.duration,
                        network: bronzeEvent.network || null,
                    },
                };
            case 'failed':
                return {
                    provider: InteractionProvider.VonageCall,
                    bronzeData: bronzeEvent,
                    type: InteractionEventType.Failed,
                    data: {
                        reason: bronzeEvent.detail,
                        errorCode: bronzeEvent.sip_code?.toString(),
                    },
                };
            case 'busy':
            case 'cancelled':
            case 'rejected':
            case 'unanswered':
                return {
                    provider: InteractionProvider.VonageCall,
                    bronzeData: bronzeEvent,
                    type: InteractionEventType.Cancelled,
                    data: {
                        reason: bronzeEvent.detail,
                        errorCode: bronzeEvent.sip_code?.toString(),
                    },
                };
            case 'machine':
                return {
                    provider: InteractionProvider.VonageCall,
                    bronzeData: bronzeEvent,
                    type: InteractionEventType.Voicemail,
                    data: {
                        subState: bronzeEvent.sub_state,
                    },
                };
            case 'human':
                return {
                    provider: InteractionProvider.VonageCall,
                    bronzeData: bronzeEvent,
                    type: InteractionEventType.HumanAnswered,
                    data: {
                        subState: bronzeEvent.sub_state,
                    },
                };
            default:
                return null;
        }
    }
    return null;
};
//# sourceMappingURL=vonage-call.js.map